import { nextTick, onBeforeUnmount, onMounted, ref, type Ref } from 'vue';

/**
 * Géométrie PARTAGÉE des liaisons d'un arbre de recherche : utilisée à la fois
 * par l'éditeur admin (AdminResearchTreeEditorView) et par l'arbre joueur
 * (ResearchTree). Évite de dupliquer le calcul des courbes SVG (la partie la
 * plus délicate / source de bugs).
 *
 * Pour chaque liaison, on lit les rects DOM des deux nœuds (repérés par
 * l'attribut `data-research-id`) et on trace une courbe TOUJOURS orientée du
 * PRÉREQUIS (source) vers le DÉPENDANT (cible) — ainsi une flèche `marker-end`
 * posée par la vue atterrit sur le dépendant et rend le sens lisible, quel que
 * soit le palier. On expose aussi `sameTier` (liaison verticale, même colonne)
 * et `backwards` (prérequis sur un palier STRICTEMENT supérieur à son
 * dépendant = progression illogique) pour que la vue les signale.
 *
 * La vue fournit `links()` (recalculé à chaque rendu de géométrie) et `tierOf`,
 * et appelle `scheduleRecompute()` quand ses données changent ; le composable
 * gère l'observation de taille + resize.
 */

export interface TreeLink<T> {
  /** Clé unique de la liaison (pour `:key` et le rendu). */
  key: string;
  /** Id du nœud prérequis (source de la flèche). */
  sourceId: number;
  /** Id du nœud dépendant (cible de la flèche). */
  targetId: number;
  /** Charge utile propre à la vue (état, libellés, id de ligne…). */
  data: T;
}

export interface TreeEdge<T> extends TreeLink<T> {
  /** Chemin SVG, TOUJOURS orienté source → dépendant. */
  d: string;
  /** Les deux nœuds sont sur le même palier (liaison verticale). */
  sameTier: boolean;
  /** Prérequis sur un palier STRICTEMENT supérieur au dépendant (sens illogique). */
  backwards: boolean;
}

export function useResearchTreeEdges<T>(
  canvasRef: Ref<HTMLElement | null>,
  links: () => TreeLink<T>[],
  tierOf: (id: number) => number,
): {
  edges: Ref<TreeEdge<T>[]>;
  svgW: Ref<number>;
  svgH: Ref<number>;
  recompute: () => void;
  scheduleRecompute: () => void;
} {
  const edges = ref<TreeEdge<T>[]>([]) as Ref<TreeEdge<T>[]>;
  const svgW = ref(0);
  const svgH = ref(0);

  // Dernier point de contrôle d'une cubique placé À DISTANCE `dist` AVANT
  // l'arrivée (ex,ey), le long de la droite (sx,sy)->(ex,ey). La tangente finale
  // = direction (contrôle -> arrivée) suit ainsi le trait -> la flèche s'oriente
  // dans le sens de la liaison (et reste horizontale si même hauteur).
  function aimControl(sx: number, sy: number, ex: number, ey: number, dist: number): [number, number] {
    const len = Math.hypot(ex - sx, ey - sy) || 1;
    return [ex - (dist * (ex - sx)) / len, ey - (dist * (ey - sy)) / len];
  }

  function recompute(): void {
    const canvas = canvasRef.value;
    if (!canvas) return;
    svgW.value = canvas.scrollWidth;
    svgH.value = canvas.scrollHeight;
    const cRect = canvas.getBoundingClientRect();
    type A = { left: number; right: number; cx: number; top: number; bottom: number; y: number };
    const c = new Map<number, A>();
    canvas.querySelectorAll<HTMLElement>('[data-research-id]').forEach((el) => {
      const id = Number(el.dataset.researchId);
      const r = el.getBoundingClientRect();
      const left = r.left - cRect.left + canvas.scrollLeft;
      const right = r.right - cRect.left + canvas.scrollLeft;
      const top = r.top - cRect.top + canvas.scrollTop;
      c.set(id, { left, right, cx: (left + right) / 2, top, bottom: top + r.height, y: top + r.height / 2 });
    });
    const out: TreeEdge<T>[] = [];
    for (const link of links()) {
      const src = c.get(link.sourceId); // prérequis (source)
      const tgt = c.get(link.targetId); // recherche dépendante (cible)
      if (!src || !tgt) continue; // hors filtre courant / hors catégorie
      const st = tierOf(link.sourceId);
      const tt = tierOf(link.targetId);
      // Chemin TOUJOURS orienté source → dépendant (la flèche atterrit sur le dépendant).
      let d: string;
      if (st === tt) {
        // Même palier (même colonne) → liaison verticale, du prérequis vers le dépendant.
        if (src.y <= tgt.y) {
          const dy = Math.max(16, (tgt.top - src.bottom) / 2);
          d = `M ${src.cx} ${src.bottom} C ${src.cx} ${src.bottom + dy}, ${tgt.cx} ${tgt.top - dy}, ${tgt.cx} ${tgt.top}`;
        } else {
          const dy = Math.max(16, (src.top - tgt.bottom) / 2);
          d = `M ${src.cx} ${src.top} C ${src.cx} ${src.top - dy}, ${tgt.cx} ${tgt.bottom + dy}, ${tgt.cx} ${tgt.bottom}`;
        }
      } else if (st < tt) {
        // Prérequis à GAUCHE (palier inférieur) → dépendant à droite : bord droit → bord gauche.
        // Sortie horizontale du prérequis ; ARRIVÉE orientée le long de la droite
        // source→cible pour que la tangente finale (et donc la flèche marker-end)
        // SUIVE le trait au lieu de rester horizontale.
        const dx = Math.max(24, (tgt.left - src.right) / 2);
        const [c2x, c2y] = aimControl(src.right, src.y, tgt.left, tgt.y, dx);
        d = `M ${src.right} ${src.y} C ${src.right + dx} ${src.y}, ${c2x} ${c2y}, ${tgt.left} ${tgt.y}`;
      } else {
        // backwards : prérequis à DROITE (palier supérieur) → dépendant à gauche.
        const dx = Math.max(24, (src.left - tgt.right) / 2);
        const [c2x, c2y] = aimControl(src.left, src.y, tgt.right, tgt.y, dx);
        d = `M ${src.left} ${src.y} C ${src.left - dx} ${src.y}, ${c2x} ${c2y}, ${tgt.right} ${tgt.y}`;
      }
      out.push({ ...link, d, sameTier: st === tt, backwards: st > tt });
    }
    edges.value = out;
  }

  let ro: ResizeObserver | null = null;
  function scheduleRecompute(): void { nextTick(() => requestAnimationFrame(recompute)); }
  onMounted(() => {
    scheduleRecompute();
    if (canvasRef.value && 'ResizeObserver' in window) {
      ro = new ResizeObserver(() => recompute());
      ro.observe(canvasRef.value);
    }
    window.addEventListener('resize', recompute);
  });
  onBeforeUnmount(() => {
    ro?.disconnect();
    window.removeEventListener('resize', recompute);
  });

  return { edges, svgW, svgH, recompute, scheduleRecompute };
}
